/* eslint-disable @typescript-eslint/default-param-last */
/* eslint-disable no-param-reassign */
import { create } from 'zustand';
import { iframeLocalStorage } from './utils/iframeLocalStorage';

type TChatState = {
  data: ChatHistory;
  status: string;
  inputsDisabled: boolean;
  currentConversationId: string;
  chatApiBaseUrl: string;
  refreshChatHistory: (() => Promise<void>) | null;
  refreshTimeout: () => void;
  refreshMessage: (message: string) => void;
  isLastMessageRerendered: string;
  showVoiceMainInput: boolean;
  noSpeechAttempts: number;
};

type TChatAction = {
  setInitialMessages: (messages: TMessageData[]) => void;
  addMessage: (message: TMessageData) => void;
  addMessageAndStartCreateAsistant: (message: TMessageData) => void;
  setSeen: () => void;
  updateLastMessage: (message: TMessageData) => void;
  setStatusOnLastMessage: (status: string) => void;
  setInputsDisabled: (value: boolean) => void;
  setReceived: () => void;
  clearMessages: () => void;
  addErrorMessage: (message: string) => void;
  setRefreshChatHistory: (refresh: (() => Promise<void>) | null) => void;
  refreshTimeout: () => void;
  refreshMessage: (message: string) => void;
  setIsLastMessageRerendered: (message: string) => void;
  setShowVoiceMainInput: (value: boolean) => void;
  setCurrentConversationId: (conversationId: string) => void;
  incrementNoSpeechAttempts: () => number;
  resetNoSpeechAttempts: () => void;
};

const newAsistantMessage = (): TMessageData => ({
  _id: Math.random().toString(36).substring(7),
  text: '',
  data: [],
  created_at: new Date().toISOString(),
  role: 'assistant',
  loading: true,
});

const newErrorMessage = (message: string): TMessageData => ({
  _id: Math.random().toString(36).substring(7),
  text: message,
  hasError: true,
  data: [],
  created_at: new Date().toISOString(),
  role: 'assistant',
  loading: false,
});

const useChatStore = create<TChatState & TChatAction>((set, get) => ({
  refreshChatHistory: null,
  data: {
    messages: [],
    reEntered: 0,
  } as ChatHistory,
  refreshTimeout: () => {
    set((state) => {
      const messages = [...state.data.messages];
      return {
        ...state,
        data: { ...state.data, messages },
        inputsDisabled: false,
      };
    });
  },
  refreshMessage: (message: string) => {
    set((state) => {
      const messages = [...state.data.messages];
      const userMessages = messages.filter((m) => m.role === 'user');
      const lastUserText =
        userMessages.length > 0 ? userMessages[userMessages.length - 1].text : '';

      iframeLocalStorage.setItem('last_user_message', lastUserText);

      messages[messages.length - 1].loading = false;
      messages[messages.length - 1].text = message;
      messages[messages.length - 1].hasError = true;
      return { ...state, data: { ...state.data, messages } };
    });
  },
  setRefreshChatHistory: (refresh) => set((state) => ({ ...state, refreshChatHistory: refresh })),
  setInitialMessages: (messages: TMessageData[]) =>
    set((state) => ({
      ...state,
      data: { ...state.data, messages },
    })),
  addErrorMessage: (message: string) =>
    set((state) => ({
      ...state,
      data: {
        ...state.data,
        messages: [...state.data.messages, newErrorMessage(message)],
      },
    })),
  clearMessages: () =>
    set((state) => ({
      ...state,
      data: { ...state.data, messages: [] },
      // TELV1-480 — a torn-down conversation must not inherit retry state
      noSpeechAttempts: 0,
    })),
  addMessage: (message: TMessageData) =>
    set((state) => ({
      ...state,
      data: { ...state.data, messages: [...state.data.messages, message] },
      inputsDisabled: true,
    })),
  addMessageAndStartCreateAsistant: (message: TMessageData) =>
    set((state) => ({
      ...state,
      data: {
        ...state.data,
        messages: [...state.data.messages, message, newAsistantMessage()],
      },
      inputsDisabled: true,
    })),
  updateLastMessage: (message: TMessageData) =>
    set((state) => {
      const messages = [...state.data.messages];
      messages[messages.length - 1] = message;
      return { ...state, data: { ...state.data, messages } };
    }),
  setSeen: () =>
    set((state) => ({
      ...state,
      data: {
        ...state.data,
        messages: state.data.messages.map((message: TMessageData) => {
          if (message.role === 'user') {
            message.seen = true;
          }
          return message;
        }),
      },
    })),
  setStatusOnLastMessage: (status: string) => {
    set((state) => {
      const messages = [...state.data.messages];
      messages[messages.length - 1].loading = false;
      messages[messages.length - 1].text = status;
      return { ...state, data: { ...state.data, messages } };
    });
  },
  setInputsDisabled: (value: boolean) => set((state) => ({ ...state, inputsDisabled: value })),
  setReceived: () =>
    set((state) => ({
      ...state,
      data: {
        ...state.data,
        messages: state.data.messages.map((message: TMessageData) => {
          if (message.role === 'user') {
            message.received = true;
          }
          return message;
        }),
      },
    })),

  setIsLastMessageRerendered: (value: string) => set({ isLastMessageRerendered: value }),

  showVoiceMainInput: true,
  setShowVoiceMainInput: (value) => set({ showVoiceMainInput: value }),

  // TELV1-480 — consecutive voice attempts rejected as "no speech". Lives
  // client-side on purpose: the backend no longer saves anything for a
  // rejected attempt and workers are stateless replicas, so there is
  // nothing server-side to count. Reset on every accepted answer.
  noSpeechAttempts: 0,
  incrementNoSpeechAttempts: () => {
    const next = get().noSpeechAttempts + 1;
    set({ noSpeechAttempts: next });
    return next;
  },
  resetNoSpeechAttempts: () => set({ noSpeechAttempts: 0 }),

  status: '',
  inputsDisabled: false,

  setCurrentConversationId: (conversationId: string) => {
    // TELV1-480 — switching conversations resets the no-speech retry counter
    set({ currentConversationId: conversationId, noSpeechAttempts: 0 });
  },
  currentConversationId: '',

  chatApiBaseUrl: '',
  isLastMessageRerendered: '',
}));

export default useChatStore;
